/**
 * Project origin lookup store.
 *
 * Hydrated by `<ProjectOriginSubscriber />` from `useProjectOriginService`
 * (the ProjectOrigin lookup, `GET /api/project-origins`); read synchronously
 * by `projectsMenu` to build the "By Origin" section.
 *
 * WHY a store (not a hook in the menu):
 *   `projectsMenu.getMenuConfig()` runs inside a `useMemo` in `MenuRenderer`,
 *   so it cannot call React Query / SDK hooks. Mirroring the sidebar badge
 *   stores (`projectBadgesStore`, `taskBadgesStore`), a thin subscriber owns
 *   the async fetch and the menu reads the result via `getState()`.
 *
 * The four canonical origins (Self / Organisation / Department / Location) and
 * their stable seeded `originID`s (1-4, migration 139) are NOT hardcoded into
 * the menu — labels, colours and ordering all come from this lookup. The store
 * seeds an empty list so the menu degrades to "no origin items" until the
 * lookup resolves, rather than guessing.
 */

import { create } from 'zustand';
import type { ProjectOrigin } from '@actschurch/ccms-sdk';

/** Minimal projection the menu needs to render an origin entry. */
export interface ProjectOriginOption {
  originID: number;
  name: string;
  color?: string | null;
}

interface ProjectOriginState {
  origins: ProjectOriginOption[];
  loaded: boolean;
  setOrigins: (rows: ProjectOrigin[]) => void;
  reset: () => void;
}

export const useProjectOriginStore = create<ProjectOriginState>((set) => ({
  origins: [],
  loaded: false,
  setOrigins: (rows) =>
    set({
      origins: (rows ?? [])
        .filter((r) => r?.originID != null)
        .map((r) => ({
          originID: r.originID,
          name: r.name || `Origin ${r.originID}`,
          color: r.color ?? null,
        })),
      loaded: true,
    }),
  reset: () => set({ origins: [], loaded: false }),
}));
